import { faAdd, faCheck, faEdit, faRemove, faSave, faTimes, faTrash } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Form, Input, Popconfirm, Select, Space, Switch, Table } from 'antd'
import AutoComplete from 'antd/lib/auto-complete'
import { useState } from 'react'
import { ProjectTypes } from '../../../common/constants'
import { hasValue } from '../../../helpers/nullable'
import VSpace from '../../common/VSpace'
import { Division } from '../../../domain/divisions/division'
import { useDeleteDivisionMutation, useDivisions, useSaveDivisionMutation } from '../../../domain/divisions/divisionsQueries'
import ToggleablePopconfirm from './ToggleablePopconfirm'
import useServiceTicketsEnabled from '../../../hooks/useServiceTicketsEnabled'

const NEW_DIVISION_ID = -1

const Divisions = () => {
  const [editingId, setEditingId] = useState<number | undefined>()
  const [addedDivision, setAddedDivision] = useState<Division[]>([])
  const [form] = Form.useForm()

  const showServiceTickets = useServiceTicketsEnabled()
  const divisionsQuery = useDivisions()
  const divisions = [...addedDivision, ...(divisionsQuery.data || [])]
  const saveDivisionMutation = useSaveDivisionMutation()
  const deleteDivisionMutation = useDeleteDivisionMutation()

  const projectTypeOptions = [
    {
      label: 'Project',
      value: ProjectTypes.Project,
    },
    ...(showServiceTickets ? [{
      label: 'Service Ticket',
      value: ProjectTypes.Ticket,
    }] : []),
    {
      label: 'Maintenance',
      value: ProjectTypes.Maintenance,
    },
  ]

  const handleAdd = () => {
    const newDivision = {
      id: NEW_DIVISION_ID,
      type: ProjectTypes.Project,
      schedulable: true,
      name: '',
      groupName: '',
    }
    setAddedDivision([newDivision])
    form.setFieldsValue(newDivision)
    editDivision(NEW_DIVISION_ID)
  }

  const cancelEdit = () => {
    if (editingId === NEW_DIVISION_ID) {
      setAddedDivision([])
    }
    setEditingId(undefined)
    form.resetFields()
  }

  const editDivision = (id: number) => {
    if (editingId) {
      cancelEdit()
    }
    setEditingId(id)
    setTimeout(() => form.resetFields(), 1)
  }

  const saveDivision = async () => {
    try {
      await form.validateFields()
      saveDivisionMutation.mutate(form.getFieldsValue())
      setEditingId(undefined)
      setAddedDivision([])
    } catch { }
  }

  const deleteDivision = (id: number) => {
    deleteDivisionMutation.mutate(id)
  }

  const shouldConfirmAdd = () => hasValue(editingId) && form.isFieldsTouched()
  const shouldConfirmEdit = (id: number) => hasValue(editingId) && editingId !== id && form.isFieldsTouched()
  const shouldConfirmCancel = () => form.isFieldsTouched()

  const editingDivision = divisions.find((x) => x.id === editingId)
  const groupNameOptions = Array.from(new Set(divisions.filter((x) => x.type === editingDivision?.type && x.groupName?.length > 0).map((x) => x.groupName)))
  groupNameOptions.sort((a, b) => a.localeCompare(b))

  return (
    <VSpace>
      <ToggleablePopconfirm
        title="Another item is already being edited. Do you want to discard those changes?"
        shouldConfirm={shouldConfirmAdd}
        onConfirm={handleAdd}
      >
        <Button
          icon={<FontAwesomeIcon icon={faAdd} />}
          type="primary"
        >
          Add Division
        </Button>
      </ToggleablePopconfirm>
      <Form
        form={form}
        initialValues={editingDivision}
      >
        <Form.Item
          hidden
          name="id"
        >
          <Input type="hidden" />
        </Form.Item>
        <Table
          dataSource={divisions}
          rowKey="id"
          columns={[
            {
              title: 'Name',
              dataIndex: 'name',
              render: (name, record) => {
                if (record.id === editingId) {
                  return (
                    <Form.Item
                      name="name"
                      rules={[{ required: true, message: 'Name is required' }]}
                      validateTrigger={['onBlur']}
                    >
                      <Input placeholder="Name" maxLength={45} />
                    </Form.Item>
                  )
                }
                return name
              },
            },
            {
              title: 'Type',
              dataIndex: 'type',
              render: (type, record) => {
                if (record.id === editingId) {
                  return (
                    <Form.Item name="type">
                      <Select
                        value={type}
                        options={projectTypeOptions}
                        popupMatchSelectWidth={false}
                      />
                    </Form.Item>
                  )
                }
                return projectTypeOptions.find((option) => option.value === type)?.label
              },
            },
            {
              title: 'Group Name',
              dataIndex: 'groupName',
              render: (groupName, record) => {
                if (record.id === editingId) {
                  return (
                    <Form.Item name="groupName">
                      <AutoComplete
                        options={groupNameOptions.map((x) => ({ value: x }))}
                        placeholder="Group Name"
                      >
                        <Input />
                      </AutoComplete>
                    </Form.Item>
                  )
                }
                return groupName
              },
            },
            {
              title: 'Schedulable',
              dataIndex: 'schedulable',
              align: 'center',
              render: (_, record) =>
                record.id === editingId ?
                  <Form.Item
                    name="schedulable"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                  : <FontAwesomeIcon icon={record.schedulable ? faCheck : faTimes} />,
            },
            {
              key: 'actions',
              render: (_, record) => (
                <Space
                  size="large"
                  style={{ marginBottom: editingId === record.id ? 24 : 0 }}
                >
                  {record.id === editingId ?
                    <>
                      <Button
                        icon={<FontAwesomeIcon icon={faSave} />}
                        type="link"
                        onClick={saveDivision}
                      />
                      <ToggleablePopconfirm
                        shouldConfirm={shouldConfirmCancel}
                        title="Are you sure you want to discard your changes?"
                        onConfirm={cancelEdit}
                      >
                        <Button
                          icon={<FontAwesomeIcon icon={faRemove} />}
                          type="link"
                          danger
                        />
                      </ToggleablePopconfirm>
                    </>
                    : <ToggleablePopconfirm
                      title="Another item is already being edited. Do you want to discard your changes?"
                      shouldConfirm={() => shouldConfirmEdit(record.id)}
                      onConfirm={() => editDivision(record.id)}
                    >
                      <Button
                        icon={<FontAwesomeIcon icon={faEdit} />}
                        type="link"
                      />
                    </ToggleablePopconfirm>
                  }
                  {record.id !== NEW_DIVISION_ID && (
                    <Popconfirm
                      title="Are you sure you want to delete this project division?"
                      onConfirm={() => deleteDivision(record.id)}
                    >
                      <Button
                        icon={<FontAwesomeIcon icon={faTrash} />}
                        danger
                        type="link"
                      />
                    </Popconfirm>
                  )}
                </Space>
              ),
            },
          ]}
          pagination={false}
        />
      </Form>
    </VSpace>
  )
}

export default Divisions
